import { CommonModule } from '@angular/common'
import { Component, Injector, OnDestroy, OnInit, runInInjectionContext } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { AbstractControl, AsyncValidatorFn, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { IntegrationService } from '@app/modules/events/services/integration.service'
import { CreateWorkspace } from '@app/modules/workspaces/interfaces/create-workspace.interface'
import { PosthogService } from '@app/services/posthog.service'
import { country, RepositoriesService } from '@app/services/repositories.service'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { ButtonSpinnerDirective } from '@app/v2/shared/button-loader.directive'
import { CountrySelectComponent } from '@app/v2/shared/components/country-select/country-select.component'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { EmailInputComponent } from '@app/v2/shared/components/inputs/email-input/email-input.component'
import { FullnameComponent } from '@app/v2/shared/components/inputs/fullname/fullname.component'
import { PhoneInputComponent } from '@app/v2/shared/components/inputs/phone-input/phone-input.component'
import { SlugComponent } from '@app/v2/shared/components/inputs/slug/slug.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { EmailFieldTypes } from '@app/v2/shared/enums/field.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { AppService } from '@app/v2/shared/services/app.service'
import { Country } from '@app/v2/shared/types/country.type'
import { EmailValidator } from '@app/v2/shared/validators/email.validator'
import { AuthService } from '@auth/services/auth.service'
import { NbCardModule } from '@nebular/theme'
import { WorkspaceService } from '@workspaces/services/workspace.service'
import { forkJoin, Observable, of, Subject, timer } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators'

/**
 * Component that is displayed after signing up to the system
 * It collects workspace details and creates the first workspace for the user
 */
@Component({
  selector: 'ngx-almost-there',
  templateUrl: './almost-there.component.html',
  styleUrls: ['./almost-there.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    PhoneInputComponent,
    FullnameComponent,
    BasicInputComponent,
    SlugComponent,
    CountrySelectComponent,
    EmailInputComponent,
    ButtonSpinnerDirective
  ]
})
export class AlmostThereComponent implements OnInit, OnDestroy {
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()

  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }
  /** Form group to collect workspace details */
  form: UntypedFormGroup
  /** Error due to invalid input */
  error: any
  public isProcessing: boolean = false
  /** Observable of filtered list of countries */
  filteredCountries$: Observable<country[] | []>
  readonly orgIcon = `<svg class='w-6 h-6 text-gray-800 dark:text-white' aria-hidden='true' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 18 18'>
<path stroke='currentColor' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M3 1h12M3 1v16M3 1H2m13 0v16m0-16h1m-1 16H3m12 0h2M3 17H1M6 4h1v1H6V4Zm5 0h1v1h-1V4ZM6 8h1v1H6V8Zm5 0h1v1h-1V8Zm-3 4h2a1 1 0 0 1 1 1v4H7v-4a1 1 0 0 1 1-1Z'/>
</svg>`
  readonly businessEmailIcon = `<svg class="w-5 h-5 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
  <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z"/>
  <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z"/>
</svg>`
  /** True when the user signed up with a business (non-free) email — the business_email field is hidden in that case */
  isUserEmailBusiness = !EmailValidator.isFreeEmail(this.authService.userEmail)
  countries: Country[] = []
  private _replayTimestamp: number
  private _lastCheckedSlug?: string
  /**
   * Creates the component & injects it's dependencies
   * @param fb FormBuilder
   * @param authService AuthService
   * @param service WorkspaceService
   * @param router Router
   * @param toastrService ToastrService
   * @param repositoriesService RepositoriesService
   */
  constructor(
    public authService: AuthService,
    private fb: UntypedFormBuilder,
    private service: WorkspaceService,
    private router: Router,
    private repositoriesService: RepositoriesService,
    private _IntegrationService: IntegrationService,
    private _appService: AppService,
    private _organizationService: OrganizationService,
    private _posthogService: PosthogService,
    private _alertService: AlertService,
    private _injector: Injector
  ) {}

  /** OnInit implementation
   * generates the form group to collect workspace information
   */
  ngOnInit() {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      slug: ['', [Validators.required], [this._uniqueSlugValidator()]],
      full_name: [this.authService.user?.full_name || '', [Validators.required]],
      phone: [this.authService.user?.phone_number || '', [Validators.required]],
      country: ['', [Validators.required]],
      financialDashboardAccessKey: [true, Validators.requiredTrue],
      marketingDashboardAccessKey: [true, Validators.requiredTrue],
      role_id: [1, Validators.required],
      email: [this.authService.userEmail],
      business_email: ['', this.isUserEmailBusiness ? [] : [Validators.required, (control) => EmailValidator.validate(control, [], EmailFieldTypes.BAN_FREE, true)]]
    })
    this.form.controls['full_name'].valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      const capitalizedFirstLetterRegex = /\b[a-z](?!\s)/g
      const nonLettersRegex = /[^a-zA-Z\u00C0-\u00FF\u0621-\u064A.\s]/g
      const moreThanOneSpaceRegex = / {2,}/g
      const moreThanOneDotRegex = /\.{2,}/g
      value = value
        .replace(nonLettersRegex, '')
        .replace(moreThanOneSpaceRegex, ' ')
        .replace(moreThanOneDotRegex, '.')
        .replace(capitalizedFirstLetterRegex, (letter) => letter.toUpperCase())
      value = value.startsWith(' ') ? value.trim() : value
      this.form.controls['full_name'].setValue(value, { emitEvent: false })
    })

    this.form.controls['name'].valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      this._organizationService.organizationNameWhileEditing.set(value)
    })
    this.form.controls['phone'].valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      const multiplePlusSigns = /\+{2,}/
      value = value?.trim().replace(multiplePlusSigns, '+')
      this.form.controls['phone'].patchValue(value, { emitEvent: false })
    })
    this.filteredCountries$ = this.form.controls['country'].valueChanges.pipe(
      map((filterString) => this.filter(filterString)),
      takeUntil(this._destroy$)
    )
    this.countries = this._appService.countries
    this._replayTimestamp = Date.now()

    const userCountryDetected = this._appService.detectedCountry() ?? this._appService.getCachedUserCountry()
    if (userCountryDetected) {
      this._applyDetectedCountry(userCountryDetected)
    } else {
      runInInjectionContext(this._injector, () =>
        toObservable(this._appService.detectedCountry)
          .pipe(
            filter((c) => !!c),
            take(1),
            takeUntil(this._destroy$)
          )
          .subscribe((c) => this._applyDetectedCountry(c!))
      )
    }
  }

  private _applyDetectedCountry(country: Country): void {
    if (!this.form.controls['country'].value) {
      this.form.controls['country'].patchValue(country)
    }
    if (!this.form.controls['phone'].value) {
      this.form.controls['phone'].patchValue(country.country_code)
      this.form.controls['phone'].markAsPristine()
      this.form.controls['phone'].markAsUntouched()
    }
  }

  /**
   * Filter countries list for the given value
   * @param value string
   * @returns
   */
  private filter(value: string) {
    return this.repositoriesService.CountriesTimezonesRepository.filter((o) => o.name.toLowerCase().includes(value.toLowerCase()))
  }

  /**
   * creates the workspace
   */
  createWorkspace() {
    if (this.isProcessing) return
    this.error = null
    if (!this.form.valid) {
      this.form.markAllAsTouched()
      return
    }
    this.isProcessing = true
    const countryName = this.form.get('country')?.value?.name
    const formattedPhone = this.form.controls.phone.value.replaceAll(' ', '')
    forkJoin([
      this.service.createWorkspace({ ...this.form.value, country: countryName } as CreateWorkspace),
      this.authService.updateUser({ full_name: this.form.value.full_name, phone_number: formattedPhone })
    ])
      .pipe(
        finalize(() => (this.isProcessing = false)),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: ([workspace, user]) => {
          this.authService.user = user
          const secondsLookBack = Math.floor((Date.now() - this._replayTimestamp) / 1000)
          const sessionReplayURL = this._posthogService.getSessionReplayURL(secondsLookBack)
          this._replayTimestamp = Date.now()
          const message = [
            {
              type: 'divider'
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: '*A new workspace has been registered with the following details:*'
              }
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: '*Workspace Name:*\n' + workspace.name
              }
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: '*Workspace Slug:*\n' + workspace.slug
              }
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: '*Owner:*\n' + user.full_name
              }
            },
            {
              type: 'section',
              fields: [
                {
                  type: 'mrkdwn',
                  text: '*Email:*\n' + user.email
                },
                {
                  type: 'mrkdwn',
                  text: '*Time:*\n' + new Date().toISOString()
                }
              ]
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: '*Phone:*\n' + user.phone_number
              }
            },
            {
              type: 'context',
              elements: [
                {
                  type: 'image',
                  image_url: 'https://cdn-icons-png.flaticon.com/512/684/684908.png', // clean pin icon
                  alt_text: 'Location'
                },
                {
                  type: 'mrkdwn',
                  text: '*Country:*\n' + countryName
                }
              ]
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: '*Session Recording:*\n<' + sessionReplayURL + '|View Replay (Public)>'
              }
            },
            {
              type: 'divider'
            }
          ]

          this._IntegrationService
            .sendSlackMessage({
              blocks: message
            })
            .pipe(take(1), takeUntil(this._destroy$))
            .subscribe()
          this.authService.workspaces = [...this.authService.workspaces, workspace]
          this.authService.currentParentWorkspace = workspace
          this.authService.currentWorkspace = workspace
          this.router.navigate(['events', 'list'])
        },
        error: (error) => {
          const err = error?.error
          let alertMsg = 'An error occurred while creating the workspace'
          if (typeof err === 'string') {
            alertMsg = err
          } else if (err?.message) {
            alertMsg = err.message
          } else if (typeof err === 'object') {
            alertMsg = Object.values(err).flat().join(' ')
          }
          if (error?.error?.business_email?.length) {
            this.form.controls['business_email'].setErrors({ serverError: error.error.business_email.join(', ') })
            this.form.controls['business_email'].markAsTouched()
          }
          this._alertService.alert(Alert.ERROR, alertMsg)
        }
      })
  }

  /**
   * Async validator to check if a slug is already taken
   * @param control Form control
   * @returns AsyncValidatorFn
   */
  private _uniqueSlugValidator(): AsyncValidatorFn {
    return (control: AbstractControl) => {
        
      if (!control.value || control.value === this._lastCheckedSlug) return of(null);

      return timer(500).pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(() => this.service.checkForDuplicateSlug(control.value)),
        tap(() => setTimeout(() => {
          this.form.updateValueAndValidity()
          this._lastCheckedSlug = control.value
        }))
      )
    }
  }
}
